import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom"
import { useEffect, useState } from "react"
import { Wordmark } from "./brand"
import { type MyCompany, api, companyStore } from "../lib/api"
import { useAuth, useSession } from "../lib/auth"
import { inDesktop } from "../lib/desktop"
import { useTheme } from "../lib/theme"
import { Moon, Sun } from "./icons"
import { useLoad } from "./ui"

// The shell: a sidebar, the signed-in company, and the credit balance.
//
// The balance sits in the chrome rather than on the billing page because it is
// the number that decides whether anything works — a user who has run out
// should learn it from the frame, not from a failed request.

const NAV = [
  { to: "/", label: "Dashboard", end: true },
  { to: "/inbox", label: "Inbox" },
  { to: "/projects", label: "Projects" },
  { to: "/time-report", label: "Time Report" },
  // Every role, external included: anyone who can log time can see their own.
  { to: "/timesheets", label: "Timesheets" },
  { to: "/calendar", label: "Calendar" },
  { to: "/workspaces", label: "Workspaces" },
  { to: "/sessions", label: "Sessions" },
  { to: "/context-hub", label: "Context Hub" },
  { to: "/docs", label: "Docs" },
  { to: "/analytics", label: "Analytics" },
  // Not admin-only since ROA-8: the page hosts personal Google Drive
  // connections, which belong to whoever signs in.
  { to: "/connections", label: "Connections" },
] as const

const ADMIN_NAV = [
  { to: "/employees", label: "Employees" },
  { to: "/credits", label: "Credits" },
  { to: "/connections", label: "Connections" },
  { to: "/migrations", label: "Migrations" },
  { to: "/workspace-servers", label: "Servers" },
] as const

// Moderniser staff, not a customer admin. Kept in its own group so nobody
// mistakes it for something their company can see.
const PLATFORM_NAV = [
  { to: "/platform/companies", label: "Companies" },
  { to: "/platform/people", label: "People" },
  { to: "/platform/analytics", label: "Analytics" },
  { to: "/platform/models", label: "Rates & models" },
  { to: "/platform/migrations", label: "Batch migration" },
  { to: "/platform/audit", label: "Audit log" },
  { to: "/platform/settings", label: "Settings" },
] as const

// Inside the desktop app, sessions run on the user's own machine — the pages
// that manage cloud workspaces and their sessions only mislead there. The nav
// hides them; the CloudOnly guard in main.tsx is what stops a typed URL.
// The external role never sees workspaces, sessions, docs, analytics or
// connections: it has developer-level visibility everywhere except those.
const HIDDEN_IN_DESKTOP = new Set<string>(["/workspaces", "/sessions", "/workspace-servers"])
const HIDDEN_FOR_EXTERNAL = new Set<string>(["/workspaces", "/sessions", "/docs", "/analytics", "/connections"])

function visible<T extends { readonly to: string }>(items: readonly T[], role: string): readonly T[] {
  if (inDesktop) items = items.filter((item) => !HIDDEN_IN_DESKTOP.has(item.to))
  if (role === "external") items = items.filter((item) => !HIDDEN_FOR_EXTERNAL.has(item.to))
  return items
}

export function Layout() {
  const { employee, company } = useSession()
  const { signOut, switchCompany, refresh } = useAuth()
  const navigate = useNavigate()
  const isAdmin = employee.role === "admin" || employee.role === "platform_admin"
  const isStaff = employee.role === "platform_admin"
  const low = company.creditsMicroUsd <= 0
  const { theme, toggleTheme } = useTheme()

  // A board of five columns, and a task page with a properties rail, both need
  // more than the 5xl the reading pages are set in. Deciding by path keeps the
  // rule in one place rather than having each page fight the shell's padding.
  const { pathname } = useLocation()
  // The report has its own wide filter bar and table, like the boards, and a
  // timesheet's seven day columns need the width to show a card's note.
  const wide = /^\/(projects\/[^/]|tasks\/|time-report|timesheets)/.test(pathname)
  // A session page owns its own height: chat, terminal and the file browser
  // each scroll inside themselves, so an outer scroll container or the reading
  // pages' padding and max width would clip them or leave them scrolling twice.
  // The project board gets the same treatment (MXX-99): it scrolls inside its
  // own area — columns sideways, cards down — under a fixed header, instead of
  // handing the tallest column's height to this outer scroll. Exact match, so
  // the settings subpage keeps the reading-page scroll.
  const fullBleed = pathname.startsWith("/workspace-sessions/") || /^\/projects\/[^/]+\/?$/.test(pathname)

  // Unread mentions. Polled rather than pushed: there is no general WebSocket
  // in this app, and 60s is the cadence of the other background check
  // (ConnectionStatus). Re-run on navigation and on a company switch too —
  // otherwise marking everything read leaves the badge up for a minute, and
  // switching company shows the previous one's count.
  const [unreadCount, setUnreadCount] = useState(0)
  useEffect(() => {
    let live = true
    async function fetchCount() {
      try {
        const res = await api.get<{ count: number }>("/mentions/unread-count")
        if (live) setUnreadCount(res.count)
      } catch {
        // A failed poll is not worth surfacing; the next one may work.
      }
    }
    fetchCount()
    const interval = setInterval(fetchCount, 60000)
    return () => {
      live = false
      clearInterval(interval)
    }
  }, [company.id, pathname])

  const memberships = useLoad(() => api.get<{ companies: MyCompany[] }>("/auth/me/companies"))
  const mine = memberships.data?.companies ?? []
  // Staff can act in a company they hold no membership in — the platform_admin
  // bypass. That state deserves a loud banner, not a quiet nav entry.
  const actingOutside = memberships.data ? !mine.some((m) => m.id === company.id) : false

  async function pick(id: string) {
    await switchCompany(id)
    navigate("/")
  }

  return (
    <div className="flex h-full flex-col">
      {actingOutside && (
        <div className="flex items-center justify-between gap-4 border-b border-warning/30 bg-warning-soft px-5 py-2 text-sm text-warning">
          <span>
            Acting as staff in <strong>{company.name}</strong> — you are not a member of this
            company.
          </span>
          <button
            type="button"
            className="shrink-0 rounded border border-warning/40 px-2 py-1 text-xs hover:bg-warning/10"
            onClick={async () => {
              // Back to their own companies: the only one, or the picker.
              companyStore.clear()
              await refresh()
              navigate("/platform/companies")
            }}
          >
            Leave
          </button>
        </div>
      )}

      <div className="flex min-h-0 flex-1">
        <aside className="flex w-60 shrink-0 flex-col border-r border-border bg-surface">
          <div className="flex flex-col gap-1 border-b border-border px-5 py-4">
            <Wordmark className="h-4 w-auto self-start text-foreground" />
            {mine.length > 1 && !actingOutside ? (
              <select
                value={company.id}
                onChange={(e) => void pick(e.target.value)}
                className="w-full truncate rounded border border-border bg-surface px-1 py-0.5 text-xs text-muted outline-none focus:border-interactive"
              >
                {mine.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </select>
            ) : (
              <span className="truncate text-xs text-muted">{company.name}</span>
            )}
          </div>

          <nav className="flex flex-1 flex-col gap-0.5 overflow-y-auto p-3">
            {visible(NAV, employee.role).map((item) => (
              <SidebarLink
                key={item.to}
                {...item}
                count={item.to === "/inbox" ? unreadCount : undefined}
              />
            ))}

            {isAdmin && (
              <>
                <p className="px-3 pt-5 pb-1.5 text-xs font-medium tracking-wide text-faint uppercase">
                  Admin
                </p>
                {visible(ADMIN_NAV, employee.role).map((item) => (
                  <SidebarLink key={item.to} {...item} />
                ))}
              </>
            )}

            {isStaff && (
              <>
                <p className="px-3 pt-5 pb-1.5 text-xs font-medium tracking-wide text-faint uppercase">
                  Platform
                </p>
                {PLATFORM_NAV.map((item) => (
                  <SidebarLink key={item.to} {...item} />
                ))}
              </>
            )}
          </nav>

          <div className="flex flex-col gap-3 border-t border-border p-3">
            <div
              className={`rounded-md px-3 py-2 ${low ? "bg-danger-soft text-danger" : "bg-surface-muted text-foreground"}`}
            >
              <p className="text-xs text-muted">Credits</p>
              <p className="font-mono text-sm font-semibold tabular-nums">{company.creditsFormatted}</p>
              {low && <p className="mt-1 text-xs">Out of credits — agents will refuse to run.</p>}
            </div>

            <div className="flex items-center justify-between gap-2 px-1">
              <div className="min-w-0">
                <p className="truncate text-sm text-foreground">{employee.name}</p>
                <p className="truncate text-xs text-muted">{employee.email}</p>
              </div>
            </div>

            <button
              type="button"
              onClick={toggleTheme}
              className="flex w-full items-center justify-center gap-2 rounded-md border border-border px-2 py-1.5 text-xs text-muted transition-colors hover:bg-surface-muted hover:text-foreground"
              title={`Switch to ${theme === "light" ? "dark" : "light"} theme`}
            >
              {theme === "light" ? <Moon /> : <Sun />}
              {theme === "light" ? "Dark theme" : "Light theme"}
            </button>

            <div className="flex gap-2">
              <NavLink
                to="/settings"
                className="flex-1 rounded-md border border-border px-2 py-1.5 text-center text-xs text-muted hover:bg-surface-muted"
              >
                Settings
              </NavLink>
              <button
                type="button"
                onClick={signOut}
                className="flex-1 rounded-md border border-border px-2 py-1.5 text-xs text-muted hover:bg-surface-muted"
              >
                Sign out
              </button>
            </div>
          </div>
        </aside>

        <main className={`flex-1 ${fullBleed ? "min-h-0 overflow-hidden" : "overflow-y-auto overflow-x-hidden"}`}>
          {fullBleed ? (
            <Outlet />
          ) : (
            <div className={`mx-auto flex flex-col gap-6 px-8 py-8 ${wide ? "max-w-[1500px]" : "max-w-5xl"}`}>
              <Outlet />
            </div>
          )}
        </main>
      </div>
    </div>
  )
}

function SidebarLink({ to, label, end, count }: { to: string; label: string; end?: boolean; count?: number }) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        `flex items-center justify-between rounded-md px-3 py-2 text-sm transition-colors ${
          isActive ? "bg-accent-soft font-medium text-accent" : "text-muted hover:bg-surface-muted hover:text-foreground"
        }`
      }
    >
      <span>{label}</span>
      {count !== undefined && count > 0 && (
        <span className="ml-2 flex h-5 min-w-5 items-center justify-center rounded-full bg-accent px-1.5 text-xs font-medium text-accent-foreground">
          {count > 99 ? "99+" : count}
        </span>
      )}
    </NavLink>
  )
}
